import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// Bump CURRENT_VERSION any time the highlights change — every signed-in user
// will then see the dialog once on their next visit, then never again.
export const WHATS_NEW_VERSION = "2026-02-wave-1-2-3";

const HIGHLIGHTS = [
  {
    title: "Head-to-head candidate comparison",
    body: "Select 2-4 candidates from the Applicants page and click 'Compare'. See résumé scores, weighted scores, per-question winners (with 'Best' badges), transcripts, and AI gap analysis side-by-side in one screen.",
    href: "/applicants",
    cta: "Try it",
  },
  {
    title: "Pipeline funnel analytics",
    body: "New widget on your Dashboard shows Applied → Invited → Opened → Started → Completed → Hired with conversion %. Toggle 7d / 30d / 90d and filter by role.",
    href: "/dashboard",
    cta: "Open Dashboard",
  },
  {
    title: "AI Co-pilot — \"Ask the candidate\"",
    body: "Open any completed Review page and ask the AI questions like 'What are this candidate's biggest strengths?' or 'Give me 3 follow-up questions'. Answers are grounded in the actual interview with question citations.",
    href: "/applicants",
    cta: "Open a review",
  },
  {
    title: "Schedule interviews on Google Calendar",
    body: "Connect Google Calendar in Settings, then click 'Schedule on Calendar' on any Review page to book a hold (with optional Google Meet link) and auto-invite the candidate.",
    href: "/settings",
    cta: "Connect Calendar",
  },
];

export default function WhatsNewDialog() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const seen = localStorage.getItem("hireloop_whats_new_version");
      if (seen !== WHATS_NEW_VERSION) {
        // Delay slightly so the dashboard renders before the modal pops.
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage disabled — silently skip */
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem("hireloop_whats_new_version", WHATS_NEW_VERSION);
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? dismiss() : setOpen(true))}>
      <DialogContent
        data-testid="whats-new-dialog"
        className="max-w-xl"
      >
        <DialogHeader>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700">
            <Sparkles className="h-3 w-3" />
            What&apos;s new in Hireloop PRO
          </div>
          <DialogTitle className="font-heading text-2xl">
            Four upgrades since you last logged in
          </DialogTitle>
          <DialogDescription>
            Quick highlights — click any tile to jump straight to that feature.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 grid gap-3">
          {HIGHLIGHTS.map((h, i) => (
            <Link
              key={h.title}
              to={h.href}
              data-testid={`whats-new-item-${i}`}
              onClick={dismiss}
              className="group block border border-slate-200 bg-white p-4 transition hover:border-blue-400 hover:bg-blue-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-sm font-semibold text-slate-900">
                    {h.title}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">{h.body}</div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 flex-none text-slate-400 transition group-hover:translate-x-1 group-hover:text-blue-700" />
              </div>
              <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700 group-hover:underline">
                {h.cta} →
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            data-testid="whats-new-dismiss"
            onClick={dismiss}
            variant="outline"
            className="rounded-sm"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
